import { createAsyncThunk } from "@reduxjs/toolkit";
import { HttpService } from "../../modules/shared/services/http.service";
import { hideLoader, showLoader } from "../Slices/loadingSlice";
import { 
  Currency, 
  CurrencyCreateDto, 
  CurrencyUpdateDto,
  BulkCurrencyCreateDto,
  CurrencyQueryParams
} from "../../modules/shared/model";

const headers = { "Content-Type": "application/json", "x-custom-lang": "en" } as const;

/**
 * Currency CRUD and Exchange Rate Actions
 * Using HttpService pattern like other action files
 */

/**
 * Create a new currency
 */
export const createCurrency = async (data: CurrencyCreateDto): Promise<Currency> => {
  const response = await HttpService.post("api/v1/currencies", data, headers, true);
  return response;
};

/**
 * Bulk create currencies
 */
export const bulkCreateCurrencies = async (data: BulkCurrencyCreateDto): Promise<Currency[]> => {
  const response = await HttpService.post("api/v1/currencies/bulk", data, headers, true);
  return response;
};

/**
 * Update currency
 */
export const updateCurrency = async (id: string, data: CurrencyUpdateDto): Promise<Currency> => {
  const response = await HttpService.patch(`api/v1/currencies/${id}`, data, headers, true);
  return response;
};

/**
 * Get currency by ID
 */
export const getCurrencyById = async (id: string): Promise<Currency> => {
  const response = await HttpService.get(`api/v1/currencies/${id}`, true, headers);
  return response;
};

/**
 * Get all currencies with pagination and filters
 */
export const getCurrencies = async (query?: CurrencyQueryParams): Promise<any> => {
  let url = "api/v1/currencies";
  
  if (query) {
    const params = new URLSearchParams();
    if (query.page) params.append("page", query.page.toString());
    if (query.limit) params.append("limit", query.limit.toString());
    if (query.name) params.append("name", query.name);
    
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;
  }
  
  const response = await HttpService.get(url, true, headers);
  return response;
};

/**
 * Delete currency
 */
export const deleteCurrency = async (id: string): Promise<void> => {
  await HttpService.deleteRecord(`api/v1/currencies/${id}`, headers, true);
};

/**
 * Convert currency amount
 */
export const convertCurrencyAmount = createAsyncThunk<
  any,
  { amount: number; fromCurrency: string; toCurrency: string; isShowLoader?: boolean }
>(
  "currencies/convert",
  async ({ amount, fromCurrency, toCurrency, isShowLoader = false }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }

      const response = await HttpService.post(
        "api/v1/currencies/convert",
        { amount, fromCurrency, toCurrency },
        headers,
        true
      );

      if (isShowLoader) {
        dispatch(hideLoader());
      }

      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorMessage = error?.data?.message || error?.message || "Failed to convert currency";
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update single currency exchange rate from ExchangeRate-API
 */
export const updateCurrencyExchangeRate = createAsyncThunk<
  any,
  { currencyId: string; isShowLoader?: boolean }
>(
  "currencies/updateExchangeRate",
  async ({ currencyId, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }

      const response = await HttpService.post(
        `api/v1/currencies/${currencyId}/update-rate`,
        {},
        headers,
        true
      );

      if (isShowLoader) {
        dispatch(hideLoader());
      }

      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorMessage = error?.data?.message || error?.message || "Failed to update exchange rate";
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update all currency exchange rates from ExchangeRate-API
 */
export const updateAllCurrencyExchangeRates = createAsyncThunk<
  any[],
  { isShowLoader?: boolean }
>(
  "currencies/updateAllExchangeRates",
  async ({ isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }

      const response = await HttpService.post(
        "api/v1/currencies/update-all-rates",
        {},
        headers,
        true
      );

      if (isShowLoader) {
        dispatch(hideLoader());
      }

      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorMessage = error?.data?.message || error?.message || "Failed to update exchange rates";
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Get ExchangeRate-API status
 */
export const getExchangeRateApiStatus = createAsyncThunk<
  {
    isConfigured: boolean;
    baseCurrency: string;
    lastUpdate?: string;
    nextUpdate?: string;
  },
  { isShowLoader?: boolean }
>(
  "currencies/getExchangeRateApiStatus",
  async ({ isShowLoader = false }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }

      const response = await HttpService.get(
        "api/v1/currencies/exchange-rate-api/status",
        true,
        headers
      );

      if (isShowLoader) {
        dispatch(hideLoader());
      }

      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorMessage = error?.data?.message || error?.message || "Failed to get API status";
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Get currency by code
 */
export const getCurrencyByCodeAction = async (currencyCode: string): Promise<any> => {
  try {
    const response = await HttpService.get(
      `api/v1/currencies/code/${currencyCode}`,
      true,
      headers
    );
    return response;
  } catch (error: any) {
    throw new Error(error?.data?.message || error?.message || "Failed to get currency by code");
  }
};
